import { BudgetModelConfigSchema, parseSubject } from '@timelog/pm-shared';
import type { BudgetModelConfig } from '@timelog/pm-shared';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { CONTROL_RESOLUTION, controlResolution, resolveBudgetModelFrom } from './budgetModel';
import type { BudgetModelFallbackReason, ResolvedBudgetModel } from './budgetModel';
import { getPostHog } from './posthogClient';

import { useSession } from '@/auth';
import { DEV_TOOLS_ENABLED } from '@/config';

/**
 * Resolves the budget model once per page load (ADR 0027) and freezes it.
 *
 * With analytics disabled the control defaults apply immediately. With a key,
 * resolution waits for the signed-in identity first — PostHog evaluates
 * variants per distinct_id, so identifying after reading the flag would hand
 * everyone the anonymous variant. FinancialsPage blocks on `ready`; the rest
 * of the app renders regardless.
 */

export type BudgetModelValue = {
  ready: boolean;
  config: ResolvedBudgetModel['config'];
  variantKey: string;
  /** Set only when this session fell back to control — which path did it. */
  reason?: BudgetModelFallbackReason;
};

const BudgetModelContext = createContext<BudgetModelValue | undefined>(undefined);

/**
 * A dev-only override of individual settings, for a local session and for
 * Playwright — which pins `VITE_POSTHOG_KEY` empty and therefore always runs
 * control, where a setting that ships `off` (the payment schedule, BR-31) could
 * never be exercised end to end. A JSON object of setting overrides under this
 * key is merged over whatever the flag resolved; anything that does not parse
 * as a full config afterwards is ignored. Folded away in a production build,
 * like the sample-data toggle (`DEV_TOOLS_ENABLED`).
 */
export const BUDGET_MODEL_OVERRIDE_KEY = 'pm.devTools.budgetModel';

const withDevOverride = (config: BudgetModelConfig): BudgetModelConfig => {
  if (!DEV_TOOLS_ENABLED) return config;
  try {
    const raw = window.localStorage.getItem(BUDGET_MODEL_OVERRIDE_KEY);
    if (!raw) return config;
    const parsed = BudgetModelConfigSchema.safeParse({ ...config, ...JSON.parse(raw) });
    return parsed.success ? parsed.data : config;
  } catch {
    return config; // no localStorage (private mode), or not JSON — the flag's config stands
  }
};

// Module-level freeze: remounts (StrictMode, route churn) never re-resolve.
let frozen: ResolvedBudgetModel | null = null;

/** Test seam — Playwright/unit code can reset the per-load freeze. */
export const resetBudgetModelForTests = (): void => {
  frozen = null;
};

export type BudgetModelProviderProps = { children: ReactNode };

export const BudgetModelProvider = ({ children }: BudgetModelProviderProps) => {
  const session = useSession();
  // Disabled analytics resolves synchronously at mount — control, frozen.
  const [resolved, setResolved] = useState<ResolvedBudgetModel | null>(() => {
    if (frozen) return frozen;
    if (getPostHog() === null) {
      const resolution = controlResolution('no-client');
      frozen = resolution;
      return resolution;
    }
    return null;
  });

  useEffect(() => {
    if (resolved) return undefined;
    const client = getPostHog();
    if (!client) return undefined; // handled at mount
    // Identity first, then flags — see the module docblock.
    if (!session.isAuthenticated || !session.user) return undefined;

    const subject = session.user.subject;
    client.identify(subject);
    try {
      client.group('tenant', parseSubject(subject).tenantId);
    } catch {
      // A malformed subject only costs the tenant grouping, never the flag.
    }

    let cancelled = false;
    void resolveBudgetModelFrom(client).then((resolution) => {
      if (cancelled) return;
      client.register({ budget_model_variant: resolution.variantKey });
      frozen = resolution;
      setResolved(resolution);
    });
    return () => {
      cancelled = true;
    };
  }, [resolved, session.isAuthenticated, session.user]);

  const value: BudgetModelValue = resolved
    ? {
        ready: true,
        config: withDevOverride(resolved.config),
        variantKey: resolved.variantKey,
        ...(resolved.reason === undefined ? {} : { reason: resolved.reason }),
      }
    : { ready: false, config: CONTROL_RESOLUTION.config, variantKey: 'control' };

  return <BudgetModelContext.Provider value={value}>{children}</BudgetModelContext.Provider>;
};

export const useBudgetModel = (): BudgetModelValue => {
  const value = useContext(BudgetModelContext);
  if (!value) {
    throw new Error('useBudgetModel must be used inside a BudgetModelProvider');
  }
  return value;
};
